import { type ReactNode, type RefObject } from 'react';
import { Button, Col, Form, InputGroup, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadCloudFileType, ObjectCustomerType } from '@econtractor/types';
import type { CloudUploadScope } from 'api/apiCloudflare';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import CustomerFormFiles from './CustomerFormFiles';
import ObjectCustomerAiPanel from './ObjectCustomerAiPanel';

interface ReadOnlyFieldProps {
    label: string;
    value?: string | boolean | null;
    yesLabel: string;
    noLabel: string;
}

const ReadOnlyField = ({ label, value, yesLabel, noLabel }: ReadOnlyFieldProps) => {
    const displayValue =
        typeof value === 'boolean' ? (value ? yesLabel : noLabel) : value ? String(value) : '-';

    return (
        <Form.Group className="mb-3">
            <label className="form-label text-muted">{label}</label>
            <div className="spec-field-value mb-0 small bg-light p-2 rounded border text-break">{displayValue}</div>
        </Form.Group>
    );
};

export interface CustomerFormProps {
    formValues: ObjectCustomerType;
    readOnly?: boolean;
    edrpouReadOnly?: boolean;
    showSaveButton?: boolean;
    isSaving?: boolean;
    isYcBusy?: boolean;
    isAiBusy?: boolean;
    ycCodeReady?: boolean;
    yesLabel: string;
    noLabel: string;
    getPersonTypeLabel: (value?: string) => string;
    handleChange: <K extends keyof ObjectCustomerType>(field: K, value: ObjectCustomerType[K]) => void;
    handleFetchYouControl: () => void;
    handleSave?: () => void;
    refInputFile?: RefObject<HTMLInputElement | null>;
    uploadProgress?: number | boolean;
    aiPhase?: 'idle' | 'uploading' | 'processing';
    processingLabel?: string;
    handleOpenClick?: () => void;
    handleChangeFile?: () => void;
    handleCancel?: () => void;
    headerSlot?: ReactNode;
    className?: string;
    customerFiles?: LeadCloudFileType[];
    clientCustomerId?: number;
    canEditFiles?: boolean;
    saveFirstMessage?: string;
    onCustomerFilesChange?: (nextFiles: LeadCloudFileType[]) => Promise<boolean>;
    onEnsureCustomerForFiles?: () => Promise<CloudUploadScope | false>;
}

const CustomerForm = ({
    formValues,
    readOnly = false,
    edrpouReadOnly = false,
    showSaveButton = true,
    isSaving = false,
    isYcBusy = false,
    isAiBusy = false,
    ycCodeReady = false,
    yesLabel,
    noLabel,
    getPersonTypeLabel,
    handleChange,
    handleFetchYouControl,
    handleSave,
    refInputFile,
    uploadProgress = false,
    aiPhase = 'idle',
    processingLabel = '',
    handleOpenClick,
    handleChangeFile,
    handleCancel,
    headerSlot,
    className = 'px-3 pb-3',
    customerFiles,
    clientCustomerId = 0,
    canEditFiles = false,
    saveFirstMessage,
    onCustomerFilesChange,
    onEnsureCustomerForFiles,
}: CustomerFormProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const filesPanel =
        customerFiles !== undefined ? (
            <CustomerFormFiles
                files={customerFiles}
                clientCustomerId={clientCustomerId}
                canEdit={!readOnly && canEditFiles}
                saveFirstMessage={saveFirstMessage || t('messSaveCustomerFirst')}
                onFilesChange={onCustomerFilesChange || (async () => true)}
                onEnsureEntity={!readOnly ? onEnsureCustomerForFiles : undefined}
            />
        ) : null;

    if (readOnly) {
        return (
            <div className={className}>
                {headerSlot}
                <ReadOnlyField label={t('customer.fields.personType')} value={getPersonTypeLabel(formValues.personType)} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField label={t('customer.fields.fullName')} value={formValues.fullName} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField label={t('customer.fields.edrpouOrIpn')} value={formValues.edrpouOrIpn} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField label={t('customer.fields.vatEnabled')} value={formValues.vatEnabled} yesLabel={yesLabel} noLabel={noLabel} />
                {formValues.vatEnabled ? (
                    <ReadOnlyField label={t('customer.fields.vatNumber')} value={formValues.vatNumber} yesLabel={yesLabel} noLabel={noLabel} />
                ) : null}
                <ReadOnlyField label={t('customer.fields.iban')} value={formValues.iban} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField label={t('customer.fields.bank')} value={formValues.bank} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField label={t('customer.fields.phone')} value={formValues.phone} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField label={t('customer.fields.email')} value={formValues.email} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField label={t('customer.fields.legalAddress')} value={formValues.legalAddress} yesLabel={yesLabel} noLabel={noLabel} />
                <ReadOnlyField
                    label={t('customer.fields.representativeName')}
                    value={formValues.representativeName}
                    yesLabel={yesLabel}
                    noLabel={noLabel}
                />
                <ReadOnlyField
                    label={t('customer.fields.representativePosition')}
                    value={formValues.representativePosition}
                    yesLabel={yesLabel}
                    noLabel={noLabel}
                />
                {filesPanel}
            </div>
        );
    }

    return (
        <div className={className}>
            {headerSlot}
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.personType')}</label>
                        <Form.Select
                            value={formValues.personType || ''}
                            onChange={(e) => handleChange('personType', e.target.value)}
                        >
                            <option value="">{t('customer.selectPlaceholder')}</option>
                            <option value="legal">{t('customer.personTypes.legal')}</option>
                            <option value="individual">{t('customer.personTypes.individual')}</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.fullName')}</label>
                        <Form.Control
                            value={formValues.fullName || ''}
                            onChange={(e) => handleChange('fullName', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.edrpouOrIpn')}</label>
                        <InputGroup>
                            <Form.Control
                                value={formValues.edrpouOrIpn || ''}
                                onChange={(e) => handleChange('edrpouOrIpn', e.target.value)}
                                disabled={edrpouReadOnly || isYcBusy}
                            />
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{t('customer.youControl.title')}</Tooltip>}
                            >
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    aria-label={t('customer.youControl.btn')}
                                    disabled={!ycCodeReady || isYcBusy || isSaving || isAiBusy}
                                    onClick={handleFetchYouControl}
                                    className="px-2"
                                >
                                    {isYcBusy ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <img
                                            src="/imgs/integrations/youcontrol.png"
                                            alt={t('customer.youControl.btn')}
                                            width={20}
                                            height={20}
                                            style={{ display: 'block' }}
                                        />
                                    )}
                                </Button>
                            </OverlayTrigger>
                        </InputGroup>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.vatEnabled')}</label>
                        <Form.Select
                            value={formValues.vatEnabled ? '1' : '0'}
                            onChange={(e) => handleChange('vatEnabled', e.target.value === '1')}
                        >
                            <option value="0">{noLabel}</option>
                            <option value="1">{yesLabel}</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                {formValues.vatEnabled ? (
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">{t('customer.fields.vatNumber')}</label>
                            <Form.Control
                                value={formValues.vatNumber || ''}
                                onChange={(e) => handleChange('vatNumber', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                ) : null}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.iban')}</label>
                        <Form.Control
                            value={formValues.iban || ''}
                            onChange={(e) => handleChange('iban', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.bank')}</label>
                        <Form.Control
                            value={formValues.bank || ''}
                            onChange={(e) => handleChange('bank', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.phone')}</label>
                        <Form.Control
                            value={formValues.phone || ''}
                            onChange={(e) => handleChange('phone', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.email')}</label>
                        <Form.Control
                            type="email"
                            value={formValues.email || ''}
                            onChange={(e) => handleChange('email', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.legalAddress')}</label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={formValues.legalAddress || ''}
                            onChange={(e) => handleChange('legalAddress', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.representativeName')}</label>
                        <Form.Control
                            value={formValues.representativeName || ''}
                            onChange={(e) => handleChange('representativeName', e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('customer.fields.representativePosition')}</label>
                        <Form.Control
                            value={formValues.representativePosition || ''}
                            onChange={(e) => handleChange('representativePosition', e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            {filesPanel}

            {handleOpenClick && handleChangeFile && handleCancel ? (
                <div className="mt-2 mb-3">
                    <ObjectCustomerAiPanel
                        isBusy={isAiBusy}
                        handleOpenClick={handleOpenClick}
                        processingLabel={processingLabel}
                        phase={aiPhase}
                        handleCancel={handleCancel}
                        uploadProgress={uploadProgress}
                        refInputFile={refInputFile}
                        handleChangeFile={handleChangeFile}
                    />
                </div>
            ) : null}

            {showSaveButton && handleSave ? (
                <div className="pt-3 text-center">
                    <Button onClick={handleSave} disabled={isSaving || isAiBusy || isYcBusy}>
                        {spinnerTitle(
                            <>
                                <SvgSave className="me-2 mtop-2" />
                                {t('common:buttons.save')}
                            </>,
                            isSaving,
                            t('common:buttons.saving'),
                        )}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

export default CustomerForm;
